import { compactNumber, marketFreshnessLabel, signedPct } from "./format";
import type { SectorCohort, StockPayload, StockPoint, StockRow } from "./types";

export type ReportCard = {
  id: "stale-data" | "unusual-volume" | "repeated-rvol" | "sector-cohorts" | "replay-snapshot";
  title: string;
  eyebrow: string;
  summary: string;
  filename: string;
  content: string;
};

function line(label: string, value: string | number | null | undefined) {
  return `${label}: ${value ?? "-"}`;
}

function rowSummary(row: StockRow) {
  return [
    row.ticker,
    line("price", row.price?.toFixed(2)),
    line("change", signedPct(row.changePct)),
    line("volume", compactNumber(row.volume)),
    line("rvol", row.relativeVolume ? `${row.relativeVolume.toFixed(2)}x` : "-")
  ].join(" | ");
}

function buildStaleDataReport(payload: StockPayload) {
  const freshness = payload.freshness;
  const source = payload.source;
  return [
    "KZG Stock House - Data Freshness Report",
    line("tradeDate", payload.tradeDate),
    line("status", marketFreshnessLabel(freshness)),
    line("expectedCompletedTradingDate", freshness?.expectedCompletedTradingDate),
    line("latestMinuteDate", freshness?.latestMinuteDate),
    line("latestDayDate", freshness?.latestDayDate),
    line("minuteLagTradingDays", freshness?.minuteLagTradingDays),
    line("dayLagTradingDays", freshness?.dayLagTradingDays),
    line("dayStatus", freshness?.dayStatus),
    line("coverage", source?.coverage),
    line("minuteFile", source?.minuteFile?.split("/").pop()),
    line("dayFile", source?.dayFile?.split("/").pop()),
    line("missingDatasets", payload.remediation?.missingDatasets.join(", ") || "none"),
    line("downloadCommand", payload.remediation?.downloadCommand),
    line("rebuildCommand", payload.remediation?.rebuildCommand),
    "",
    "Remediation:",
    payload.remediation?.downloadCommand
      ? `1. Run ${payload.remediation.downloadCommand}`
      : "1. No backfill command is needed for the current payload.",
    `2. Run ${payload.remediation?.rebuildCommand ?? "npm run data:build && npm run test && npm run build"}`,
    "3. Run npm run smoke against preview before deploy."
  ].join("\n");
}

function buildUnusualVolumeReport(payload: StockPayload) {
  const rows = [...(payload.unusualVolume ?? [])]
    .sort((a, b) => (b.relativeVolume ?? 0) - (a.relativeVolume ?? 0))
    .slice(0, 12);
  return [
    "KZG Stock House - Top Unusual Volume",
    line("tradeDate", payload.tradeDate),
    line("totalUnusualVolumeNames", payload.summary?.unusualVolumeCount),
    "",
    rows.length ? rows.map((row, index) => `${index + 1}. ${rowSummary(row)}`).join("\n") : "No unusual-volume rows in payload."
  ].join("\n");
}

function buildRepeatedRvolReport(payload: StockPayload) {
  const rows = payload.repeatedUnusualVolume ?? [];
  return [
    "KZG Stock House - Repeated RVOL Scanner",
    line("tradeDate", payload.tradeDate),
    line("scannerRows", rows.length),
    "",
    rows.length
      ? rows
          .slice(0, 16)
          .map((row, index) => {
            const path = row.events
              .map((event) => `${event.date}:${event.relativeVolume?.toFixed(2) ?? "-"}x`)
              .join(" -> ");
            return `${index + 1}. ${row.ticker} | hits: ${row.occurrences} | latest: ${
              row.latestRelativeVolume?.toFixed(2) ?? "-"
            }x | max: ${row.maxRelativeVolume?.toFixed(2) ?? "-"}x | gap: ${
              row.gapPct?.toFixed(2) ?? "-"
            }% | aboveVWAP: ${row.aboveVwap ? "yes" : "no"} | nearHigh: ${row.nearSessionHigh ? "yes" : "no"} | ${path}`;
          })
          .join("\n")
      : "No repeated RVOL names in payload."
  ].join("\n");
}

function cohortSummary(cohort: SectorCohort) {
  return [
    cohort.sector,
    line("proxy", cohort.proxyTicker),
    line("proxyChange", signedPct(cohort.proxyChangePct)),
    line("avgChange", signedPct(cohort.avgChangePct)),
    line("tickers", cohort.tickerCount),
    line("advancing", cohort.advancing),
    line("declining", cohort.declining),
    line("volume", compactNumber(cohort.totalVolume)),
    line("turnover", compactNumber(cohort.turnover))
  ].join(" | ");
}

function targetSummary(target: NonNullable<NonNullable<StockPayload["sectorMetadataCoverage"]>["unmappedTargets"]>[number]) {
  return [
    target.ticker,
    line("price", target.price?.toFixed(2)),
    line("change", signedPct(target.changePct)),
    line("volume", compactNumber(target.volume)),
    line("rvol", target.relativeVolume ? `${target.relativeVolume.toFixed(2)}x` : "-"),
    line("turnover", compactNumber(target.turnover, true))
  ].join(" | ");
}

function buildSectorCohortsReport(payload: StockPayload) {
  const cohorts = [...(payload.sectorCohorts ?? [])].sort((a, b) => b.totalVolume - a.totalVolume);
  const coverage = payload.sectorMetadataCoverage;
  const metadataRows = coverage?.metadataRows ?? cohorts.reduce((total, cohort) => total + cohort.tickerCount, 0);
  const pct = (value: number | null | undefined) => (value === null || value === undefined ? "-" : `${value.toFixed(2)}%`);
  return [
    "KZG Stock House - Sector Cohort Summary",
    line("tradeDate", payload.tradeDate),
    line("sectorCohorts", cohorts.length),
    line("metadataRows", metadataRows),
    line("matchedSymbols", coverage ? `${coverage.matchedSymbols} / ${coverage.totalSymbols}` : "-"),
    line("symbolCoveragePct", pct(coverage?.symbolCoveragePct)),
    line("matchedVolume", coverage ? compactNumber(coverage.matchedVolume) : "-"),
    line("volumeCoveragePct", pct(coverage?.volumeCoveragePct)),
    line("topUnmappedByVolume", coverage?.unmappedTopSymbols.join(", ") || "-"),
    "",
    "Top metadata targets:",
    coverage?.unmappedTargets?.length
      ? coverage.unmappedTargets.map((target, index) => `${index + 1}. ${targetSummary(target)}`).join("\n")
      : "No unmapped target details in payload.",
    "",
    cohorts.length
      ? cohorts.map((cohort, index) => `${index + 1}. ${cohortSummary(cohort)}`).join("\n")
      : "No sector cohort metadata in payload."
  ].join("\n");
}

function pointSummary(point: StockPoint) {
  return `${point.time} | open ${point.open.toFixed(2)} | high ${point.high.toFixed(2)} | low ${point.low.toFixed(
    2
  )} | close ${point.close.toFixed(2)} | volume ${compactNumber(point.volume)}`;
}

function buildReplaySnapshot(payload: StockPayload, selected: StockRow | null, activeMinute: StockPoint | null) {
  if (!selected) {
    return ["KZG Stock House - Selected Symbol Replay", line("tradeDate", payload.tradeDate), "No selected symbol."].join(
      "\n"
    );
  }
  const topMinutes = [...(selected.points ?? [])].sort((a, b) => b.volume - a.volume).slice(0, 8);
  return [
    "KZG Stock House - Selected Symbol Replay",
    line("tradeDate", payload.tradeDate),
    rowSummary(selected),
    line("vwap", selected.vwap?.toFixed(2)),
    line("range", signedPct(selected.rangePct)),
    line("activeMinute", activeMinute ? pointSummary(activeMinute) : "summary-only symbol"),
    "",
    "Top volume minutes:",
    topMinutes.length ? topMinutes.map((point, index) => `${index + 1}. ${pointSummary(point)}`).join("\n") : "No packed minute trace."
  ].join("\n");
}

export function buildReportCards(
  payload: StockPayload | null,
  selected: StockRow | null,
  activeMinute: StockPoint | null,
  repeatedRows = payload?.repeatedUnusualVolume ?? []
): ReportCard[] {
  if (!payload) return [];
  const date = payload.tradeDate ?? "unbuilt";
  const unusualCount = payload.summary?.unusualVolumeCount ?? 0;
  return [
    {
      id: "stale-data",
      title: "Freshness Brief",
      eyebrow: marketFreshnessLabel(payload.freshness),
      summary: `Minute lag ${payload.freshness?.minuteLagTradingDays ?? "-"} sessions; day lag ${
        payload.freshness?.dayLagTradingDays ?? "-"
      } sessions.`,
      filename: `kzg-stock-house-freshness-${date}.txt`,
      content: buildStaleDataReport(payload)
    },
    {
      id: "unusual-volume",
      title: "Unusual Volume List",
      eyebrow: `${unusualCount.toLocaleString("en-US")} names`,
      summary: `Ranks the strongest relative-volume names from the ${date} local Massive payload.`,
      filename: `kzg-stock-house-unusual-volume-${date}.txt`,
      content: buildUnusualVolumeReport(payload)
    },
    {
      id: "repeated-rvol",
      title: "Repeated RVOL Scanner",
      eyebrow: `${repeatedRows.length.toLocaleString("en-US")} repeaters`,
      summary: "Flags names that produced unusual relative volume across multiple sessions.",
      filename: `kzg-stock-house-repeated-rvol-${date}.txt`,
      content: buildRepeatedRvolReport({ ...payload, repeatedUnusualVolume: repeatedRows })
    },
    {
      id: "sector-cohorts",
      title: "Sector Cohort Summary",
      eyebrow: `${(payload.sectorCohorts ?? []).length.toLocaleString("en-US")} cohorts`,
      summary: "Exports the sector ETF metadata seed as an auditable breadth and volume brief.",
      filename: `kzg-stock-house-sector-cohorts-${date}.txt`,
      content: buildSectorCohortsReport(payload)
    },
    {
      id: "replay-snapshot",
      title: "Replay Snapshot",
      eyebrow: selected?.ticker ?? "No symbol",
      summary: selected?.points?.length
        ? `${selected.ticker} has ${selected.points.length.toLocaleString("en-US")} packed minute candles.`
        : "Selected row is summary-only until packed into the first-screen trace set.",
      filename: `kzg-stock-house-replay-${selected?.ticker ?? "none"}-${date}.txt`,
      content: buildReplaySnapshot(payload, selected, activeMinute)
    }
  ];
}
